'use client';

import { useEffect, useRef } from 'react';
import Link from 'next/link';

const steps = [
  {
    step: '01',
    title: 'Book Your Repair',
    desc: "Fill out our quick booking form with your device, issue, and preferred time. We'll confirm within 30 minutes.",
    icon: 'M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013 12.79 19.79 19.79 0 01.21 4.18 2 2 0 012.18 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.91 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z',
    color: '#1E90FF',
  },
  {
    step: '02',
    title: 'We Come to You',
    desc: 'Our certified technician arrives at your home, office, or any convenient location in the KWC at the agreed time.',
    icon: 'M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z',
    color: '#FF6F00',
  },
  {
    step: '03',
    title: 'Repair & Test',
    desc: 'We diagnose, repair, and test your device on-site using genuine parts. Most repairs take 30 - 120 minutes.',
    icon: 'M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z',
    color: '#22C55E',
  },
  {
    step: '04',
    title: 'Pay & Enjoy',
    desc: "Pay only after you're satisfied. We accept cash, e-transfer, and card. Your device comes with a 60-day warranty.",
    icon: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
    color: '#8B5CF6',
  },
];

export default function HowItWorks() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef?.current?.querySelectorAll('.reveal, .reveal-scale');
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section data-component-id="src/components/HowItWorks.tsx:55:4" data-component-path="src/components/HowItWorks.tsx" data-component-line="55" data-component-end-line="206" data-component-file="HowItWorks.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-24%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D"
      ref={sectionRef}
      className="py-24 px-4 sm:px-6 lg:px-8"
      style={{ background: '#F8FAFF' }}
    >
      <div data-component-id="src/components/HowItWorks.tsx:60:6" data-component-path="src/components/HowItWorks.tsx" data-component-line="60" data-component-end-line="205" data-component-file="HowItWorks.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%22%7D" className="max-w-7xl mx-auto">
        {/* Header */}
        <div data-component-id="src/components/HowItWorks.tsx:62:8" data-component-path="src/components/HowItWorks.tsx" data-component-line="62" data-component-end-line="96" data-component-file="HowItWorks.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-16%22%7D" className="text-center mb-16">
          <div data-component-id="src/components/HowItWorks.tsx:63:10" data-component-path="src/components/HowItWorks.tsx" data-component-line="63" data-component-end-line="78" data-component-file="HowItWorks.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22section-tag%20mb-4%20reveal%22%2C%22textContent%22%3A%22How%20It%20Works%22%7D" className="section-tag mb-4 reveal">
            <svg data-component-id="src/components/HowItWorks.tsx:64:12" data-component-path="src/components/HowItWorks.tsx" data-component-line="64" data-component-end-line="76" data-component-file="HowItWorks.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%7D"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle data-component-id="src/components/HowItWorks.tsx:74:14" data-component-path="src/components/HowItWorks.tsx" data-component-line="74" data-component-end-line="74" data-component-file="HowItWorks.tsx" data-component-name="circle" data-component-content="%7B%22elementName%22%3A%22circle%22%7D" cx="12" cy="12" r="10" />
              <polyline data-component-id="src/components/HowItWorks.tsx:75:14" data-component-path="src/components/HowItWorks.tsx" data-component-line="75" data-component-end-line="75" data-component-file="HowItWorks.tsx" data-component-name="polyline" data-component-content="%7B%22elementName%22%3A%22polyline%22%7D" points="12 6 12 12 16 14" />
            </svg>
            How It Works
          </div>
          <h2 data-component-id="src/components/HowItWorks.tsx:79:10" data-component-path="src/components/HowItWorks.tsx" data-component-line="79" data-component-end-line="88" data-component-file="HowItWorks.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-4xl%20sm%3Atext-5xl%20font-heading%20font-extrabold%20mb-4%20reveal%22%2C%22textContent%22%3A%22Repair%20in%204%20Simple%20Steps%22%7D"
            className="text-4xl sm:text-5xl font-heading font-extrabold mb-4 reveal"
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 800,
              color: '#1A1F2E',
            }}
          >
            Repair in 4 Simple Steps
          </h2>
          <p data-component-id="src/components/HowItWorks.tsx:89:10" data-component-path="src/components/HowItWorks.tsx" data-component-line="89" data-component-end-line="95" data-component-file="HowItWorks.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20max-w-1xl%20mx-auto%20reveal%22%2C%22textContent%22%3A%22No%20store%20visits%2C%20no%20waiting%20in%20line.%20We%20make%20mobile%20repair%20as%20simple%20as%20ordering%20a%5Cn%20%20%20%20%20%20%20%20%20%20%20%20pizza.%20Fast%2C%20safe%2C%20and%20hassle-free.%22%7D"
            className="text-lg max-w-1xl mx-auto reveal"
            style={{ color: '#6B7280', fontFamily: "'DM Sans', sans-serif" }}
          >
            No store visits, no waiting in line. We make mobile repair as simple as ordering a
            pizza. Fast, safe, and hassle-free.
          </p>
        </div>

        {/* Steps */}
        <div data-component-id="src/components/HowItWorks.tsx:99:8" data-component-path="src/components/HowItWorks.tsx" data-component-line="99" data-component-end-line="176" data-component-file="HowItWorks.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20sm%3Agrid-cols-2%20lg%3Agrid-cols-4%20gap-6%20mb-16%22%7D" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {steps?.map((step, i) => (
            <div data-component-id="src/components/HowItWorks.tsx:101:12" data-component-path="src/components/HowItWorks.tsx" data-component-line="101" data-component-end-line="174" data-component-file="HowItWorks.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20reveal-scale%22%7D"
              key={step?.step}
              className="relative reveal-scale"
              style={{ transitionDelay: `${i * 0.1}s` }}
            >
              {/* Connector line */}
              {i < steps?.length - 1 && (
                <div data-component-id="src/components/HowItWorks.tsx:108:16" data-component-path="src/components/HowItWorks.tsx" data-component-line="108" data-component-end-line="116" data-component-file="HowItWorks.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22hidden%20lg%3Ablock%20absolute%20top-10%20left-full%20w-full%20h-0.5%20z-0%22%7D"
                  className="hidden lg:block absolute top-10 left-full w-full h-0.5 z-0"
                  style={{
                    background: 'linear-gradient(90deg, #1E90FF, #FF6F00)',
                    width: 'calc(100% - 64px)',
                    left: 'calc(50% + 32px)',
                    top: '50%',
                  }}
                />
              )}

              <div data-component-id="src/components/HowItWorks.tsx:119:14" data-component-path="src/components/HowItWorks.tsx" data-component-line="119" data-component-end-line="173" data-component-file="HowItWorks.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20z-10%20bg-white%20rounded-3xl%20p-7%20text-center%20h-full%22%7D"
                className="relative z-10 bg-white rounded-3xl p-7 text-center h-full"
                style={{
                  border: '1px solid rgba(30, 144, 255, 0.1)',
                  boxShadow: '0 4px 24px rgba(30, 144, 255, 0.06)',
                }}
              >
                {/* Step number */}
                <div data-component-id="src/components/HowItWorks.tsx:127:16" data-component-path="src/components/HowItWorks.tsx" data-component-line="127" data-component-end-line="143" data-component-file="HowItWorks.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-14%20h-14%20rounded-2xl%20flex%20items-center%20justify-center%20mx-auto%20mb-5%22%7D"
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5"
                  style={{ background: `${step?.color}15`, border: `2px solid ${step?.color}30` }}
                >
                  <svg data-component-id="src/components/HowItWorks.tsx:131:18" data-component-path="src/components/HowItWorks.tsx" data-component-line="131" data-component-end-line="142" data-component-file="HowItWorks.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%7D"
                    width="26"
                    height="26"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={step?.color}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path data-component-id="src/components/HowItWorks.tsx:141:20" data-component-path="src/components/HowItWorks.tsx" data-component-line="141" data-component-end-line="141" data-component-file="HowItWorks.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" d={step?.icon} />
                  </svg>
                </div>

                <div data-component-id="src/components/HowItWorks.tsx:145:16" data-component-path="src/components/HowItWorks.tsx" data-component-line="145" data-component-end-line="154" data-component-file="HowItWorks.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-xs%20font-heading%20font-800%20mb-2%20uppercase%20tracking-widest%22%2C%22textContent%22%3A%22Step%22%7D"
                  className="text-xs font-heading font-800 mb-2 uppercase tracking-widest"
                  style={{
                    color: step?.color,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: 800,
                  }}
                >
                  Step {step?.step}
                </div>

                <h3 data-component-id="src/components/HowItWorks.tsx:156:16" data-component-path="src/components/HowItWorks.tsx" data-component-line="156" data-component-end-line="165" data-component-file="HowItWorks.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-lg%20font-heading%20font-700%20mb-3%22%7D"
                  className="text-lg font-heading font-700 mb-3"
                  style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: 700,
                    color: '#1A1F2E',
                  }}
                >
                  {step?.title}
                </h3>

                <p data-component-id="src/components/HowItWorks.tsx:167:16" data-component-path="src/components/HowItWorks.tsx" data-component-line="167" data-component-end-line="172" data-component-file="HowItWorks.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20leading-relaxed%22%7D"
                  className="text-sm leading-relaxed"
                  style={{ color: '#6B7280', fontFamily: "'DM Sans', sans-serif" }}
                >
                  {step?.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div data-component-id="src/components/HowItWorks.tsx:179:8" data-component-path="src/components/HowItWorks.tsx" data-component-line="179" data-component-end-line="204" data-component-file="HowItWorks.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20reveal%22%7D" className="text-center reveal">
          <Link data-component-id="src/components/HowItWorks.tsx:180:10" data-component-path="src/components/HowItWorks.tsx" data-component-line="180" data-component-end-line="203" data-component-file="HowItWorks.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Fbook-repair%22%2C%22className%22%3A%22btn-accent%20text-base%20px-10%20py-4%22%7D"
            href="/book-repair"
            className="btn-accent text-base px-10 py-4"
            style={{
              background: 'linear-gradient(135deg, #FF6F00, #FF9A3C)',
              boxShadow: '0 6px 24px rgba(255, 111, 0, 0.4)',
            }}
          >
            <>
              <svg data-component-id="src/components/HowItWorks.tsx:189:14" data-component-path="src/components/HowItWorks.tsx" data-component-line="189" data-component-end-line="200" data-component-file="HowItWorks.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%7D"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path data-component-id="src/components/HowItWorks.tsx:199:16" data-component-path="src/components/HowItWorks.tsx" data-component-line="199" data-component-end-line="199" data-component-file="HowItWorks.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013 12.79 19.79 19.79 0 01.21 4.18 2 2 0 012.18 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.91 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
              </svg>
              Book Your Repair Now
            </>
          </Link>
        </div>
      </div>
    </section>
  );
}
